import type { Maybe } from "@/domains/common/types";
import { Uuid } from "@/domains/global/identifiers";
import { AssistantSyncModelObservable } from "@/store/assistant/AssistantSyncModelObservable";
import type { AppStore } from "@/store/AppStore";
import { BaseSyncModelStore } from "@/store/sync/BaseSyncModelStore";
import {
  OptimisticSyncUpdate,
  SyncModelData,
  SyncModelKind,
  SyncUpdateValue,
} from "@/store/sync/types";
import { AppSubStoreArgs } from "@/store/types";
import { makeObservable, override } from "mobx";

type ComputeAssistantIndexes<ModelData extends SyncModelData> = (
  modelData: ModelData | undefined
) => Record<string, unknown>;

export class AppStoreAssistantSyncModelStore<
  ModelData extends SyncModelData,
> extends BaseSyncModelStore<AssistantSyncModelObservable<ModelData>, ModelData> {
  private readonly computeModelIndexes: ComputeAssistantIndexes<ModelData>;

  constructor({
    modelKind,
    computeModelIndexes,
    ...injectedDeps
  }: {
    modelKind: SyncModelKind;
    computeModelIndexes?: ComputeAssistantIndexes<ModelData>;
  } & AppSubStoreArgs) {
    super({ modelKind, ...injectedDeps });
    this.computeModelIndexes = computeModelIndexes ?? (() => ({}));

    makeObservable<this, "computeModelIndexes" | "createSyncModel">(this, {
      computeModelIndexes: false,
      createSyncModel: false,
      computeIndexes: override,
      getAssistantSyncModelObservableById: false,
    });
  }

  createSyncModel(data: SyncUpdateValue<ModelData>): AssistantSyncModelObservable<ModelData> {
    return new AssistantSyncModelObservable({
      id: data.model_id,
      data,
      modelKind: this.modelKind,
      store: this.store,
    });
  }

  public computeIndexes({
    remoteData,
    optimisticUpdates,
  }: {
    store: AppStore;
    remoteData: Maybe<SyncUpdateValue<ModelData>>;
    optimisticUpdates: OptimisticSyncUpdate<ModelData>[];
  }): Record<string, unknown> {
    const syncUpdateValue =
      (optimisticUpdates.at(-1)?.value as SyncUpdateValue<ModelData> | undefined) || remoteData;

    return this.computeModelIndexes(syncUpdateValue?.model_data);
  }

  getAssistantSyncModelObservableById({
    assistantSyncModelId,
  }: {
    assistantSyncModelId: Uuid;
  }): AssistantSyncModelObservable<ModelData> | undefined {
    return this.get(assistantSyncModelId);
  }
}
